import { Check, ChevronsUpDown } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { ENV_OPTIONS, useEnvironment } from '../hooks/useEnvironment';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { cn } from '../lib/utils';

/**
 * Environment indicator + switcher in the header (BOT-392).
 *
 * Greenlight runs one deployment per environment, so switching is a navigation
 * to the sibling host at the same path — not an in-app data toggle. The control
 * is presented as a picker because that is how operators think about it
 * ("am I in sandbox or production?"), and it answers the review's ask to make
 * the distinction visible and switchable in a click.
 */
export default function EnvSwitcher() {
  const { env, isStaging, label } = useEnvironment();
  const { pathname, search } = useLocation();
  const path = `${pathname}${search}`;

  const dotFor = (e: string) =>
    e === 'staging' ? 'bg-amber-500' : e === 'production' ? 'bg-green-500' : 'bg-zinc-400';

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          title="Switch environment"
          className={cn(
            'flex h-7 shrink-0 select-none items-center gap-[7px] rounded-md border pl-[9px] pr-2 text-xs font-semibold tracking-[0.01em]',
            'hover:brightness-[0.97] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1',
            isStaging
              ? 'border-amber-200 bg-amber-50 text-amber-800'
              : 'border-border bg-secondary text-zinc-700',
          )}
        >
          <span className={cn('h-1.5 w-1.5 rounded-full', dotFor(env))} />
          {label}
          <ChevronsUpDown className="h-3 w-3 opacity-60" />
        </button>
      </PopoverTrigger>

      <PopoverContent className="w-[268px] p-1">
        <div className="px-2 py-1.5 text-[11px] font-semibold uppercase tracking-[0.04em] text-zinc-400">
          Environment
        </div>
        {ENV_OPTIONS.map(option => {
          const current = option.env === env;
          return (
            <a
              key={option.env}
              href={current ? undefined : option.href(path)}
              aria-current={current ? 'true' : undefined}
              className={cn(
                'flex items-start gap-[9px] rounded-md px-2 py-[7px]',
                current ? 'bg-secondary' : 'hover:bg-accent',
              )}
            >
              <span className={cn('mt-[5px] h-[7px] w-[7px] shrink-0 rounded-full', dotFor(option.env))} />
              <span className="flex-1 leading-[1.35]">
                <span className="block text-[13px] font-medium text-foreground">{option.label}</span>
                <span className="block text-[11.5px] text-muted-foreground">{option.hint}</span>
              </span>
              {current && <Check className="mt-0.5 h-3.5 w-3.5 shrink-0" strokeWidth={2.5} />}
            </a>
          );
        })}
      </PopoverContent>
    </Popover>
  );
}
